// Notifications center — list + filter + mark-read + delete.
// Lives at /notifications inside the authed layout.

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Trash2, RefreshCw, Loader2, Filter } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = {
  new_lead: "New Lead",
  task_approval_required: "Task Approval",
  task_assigned: "Task Assigned",
  follow_up_reminder: "Follow-up",
  lead_status_changed: "Lead Status",
  generic: "General",
};

function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (!d.getTime()) return "";
  return d.toLocaleString();
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unread, setUnread] = useState(0);
  const [filterType, setFilterType] = useState("all");
  const [filterRead, setFilterRead] = useState("all");
  const [since, setSince] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: "200" });
      if (filterType !== "all") p.set("type", filterType);
      if (filterRead === "unread") p.set("unread_only", "1");
      if (since) p.set("since", new Date(since).toISOString());
      const r = await fetch(`${BACKEND_URL}/api/notifications?${p}`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        let rows = d.notifications || [];
        if (filterRead === "read") rows = rows.filter((n) => n.read);
        setItems(rows);
        setUnread(d.total_unread || 0);
      } else {
        toast.error("Failed to load notifications");
      }
    } catch (e) {
      toast.error("Failed to load notifications");
    } finally { setLoading(false); }
  }, [filterType, filterRead, since]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleOpen = (n) => {
    if (!n.read) {
      setItems((prev) => prev.map((x) => x.notification_id === n.notification_id ? { ...x, read: true } : x));
      setUnread((u) => Math.max(0, u - 1));
      fetch(`${BACKEND_URL}/api/notifications/${n.notification_id}/read`, { method: "PATCH", credentials: "include" }).catch(() => {});
    }
    if (n.link) navigate(n.link);
  };

  const handleMarkRead = async (n, e) => {
    e?.stopPropagation();
    setItems((prev) => prev.map((x) => x.notification_id === n.notification_id ? { ...x, read: true } : x));
    setUnread((u) => Math.max(0, u - 1));
    try { await fetch(`${BACKEND_URL}/api/notifications/${n.notification_id}/read`, { method: "PATCH", credentials: "include" }); }
    catch (_) {}
  };

  const handleDelete = async (n, e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete this notification?")) return;
    setItems((prev) => prev.filter((x) => x.notification_id !== n.notification_id));
    if (!n.read) setUnread((u) => Math.max(0, u - 1));
    try { await fetch(`${BACKEND_URL}/api/notifications/${n.notification_id}`, { method: "DELETE", credentials: "include" }); }
    catch (_) { toast.error("Delete may have failed — refresh to confirm"); }
  };

  const handleMarkAllRead = async () => {
    if (unread === 0) return;
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    setUnread(0);
    try {
      const r = await fetch(`${BACKEND_URL}/api/notifications/mark-all-read`, { method: "POST", credentials: "include" });
      if (r.ok) toast.success("All notifications marked as read");
    } catch (_) {}
  };

  const typeAccent = (type) => {
    if (type === "new_lead") return "bg-emerald-500";
    if (type === "task_approval_required" || type === "task_assigned") return "bg-violet-500";
    if (type === "follow_up_reminder") return "bg-amber-500";
    return "bg-slate-400";
  };

  return (
    <div className="space-y-4 overflow-x-hidden" data-testid="notifications-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <Bell className="w-7 h-7 text-indigo-600" />
            Notifications
            {unread > 0 && (
              <span className="text-xs bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full font-semibold">{unread} unread</span>
            )}
          </h1>
          <p className="text-sm text-slate-500 mt-1">Tap to open · refresh every 20 seconds when bell badge is visible</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} data-testid="refresh-notifications">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button size="sm" onClick={handleMarkAllRead} disabled={unread === 0} className="bg-indigo-600 hover:bg-indigo-700">
            <CheckCheck className="w-4 h-4 mr-1" /> Mark all read
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1"><Filter className="w-3 h-3" /> Type</label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {Object.entries(TYPE_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Status</label>
              <Select value={filterRead} onValueChange={setFilterRead}>
                <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="unread">Unread only</SelectItem>
                  <SelectItem value="read">Read only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Since</label>
              <Input type="date" value={since} onChange={(e) => setSince(e.target.value)} className="h-9 mt-1" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading && items.length === 0 ? (
            <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-500" /></div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-400">
              <Bell className="w-12 h-12 mx-auto opacity-30 mb-3" />
              No notifications match the current filters
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-700/50">
              {items.map((n) => (
                <li
                  key={n.notification_id}
                  onClick={() => handleOpen(n)}
                  className={`px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700/30 cursor-pointer flex gap-3 ${!n.read ? "bg-indigo-50/40 dark:bg-indigo-900/10" : ""}`}
                  data-testid={`notification-row-${n.notification_id}`}
                >
                  <div className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${typeAccent(n.type)}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <p className={`text-sm leading-tight ${!n.read ? "font-semibold text-slate-900 dark:text-white" : "text-slate-700 dark:text-slate-300"}`}>
                        {n.title}
                      </p>
                      <span className="text-[10px] uppercase tracking-wider text-slate-400 shrink-0 hidden sm:inline">
                        {TYPE_LABELS[n.type] || n.type}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-0.5">{n.message}</p>
                    <p className="text-[11px] text-slate-400 mt-1">{formatDateTime(n.created_at)}</p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-1 shrink-0">
                    {!n.read && (
                      <Button variant="ghost" size="sm" onClick={(e) => handleMarkRead(n, e)} className="h-7 px-2 text-xs" title="Mark read">
                        <CheckCheck className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={(e) => handleDelete(n, e)} className="h-7 px-2 text-rose-500 hover:bg-rose-50 hover:text-rose-700" title="Delete">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
